import React, {useState} from 'react';
import {styled} from '@mui/material/styles';
import {useHistory} from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import MenuDrawer from './MenuDrawer';
import AuthService from '../../auth/services/AuthService';
import {BluePillButton} from '../../common/components/BluePillButton';

import '../assets/fonts/AlpinGothic/stylesheet.css';
import '../assets/fonts/Montserrat/stylesheet.css';
import '../assets/fonts/ProximaNova/stylesheet.css';

import themeStyles from '../assets/stylesheets/ufli-theme.module.css';
import baseStyles from '../../css/Base.module.css';

import ImageLogo from '../assets/images/logos/flamingo/lockup/dark.svg';
import ImageStudent from '../assets/images/undraw-student.svg';

const PREFIX = 'Home';

const classes = {
  iconButton: `${PREFIX}-iconButton`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')({
  [`& .${classes.iconButton}`]: {
    backgroundColor: '#EE4028',
    '&:hover': {
      backgroundColor: '#f26a57',
    },
  },
});

export default function Home() {
  const [isMenuOpen, toggleMenu] = useState(false);

  const history = useHistory();

  async function handleClickAvatar() {
    const {isAuthenticated} = await AuthService.checkAuthentication();
    if (isAuthenticated) history.push('/builder');
    else toggleMenu(true);
  }

  return (
    <Root>
      <AppBar position="sticky" className={'bg-white'}>
        <Toolbar>
          <BluePillButton type="submit" variant="outlined" href="/student/login">
            HAVE A JOIN CODE?
          </BluePillButton>
          <Typography variant="h6" className={baseStyles['f-g-1']}></Typography> {/* Spacer */}
          <IconButton color="inherit" className={classes.iconButton} onClick={handleClickAvatar} size="large">
            <PersonOutlineOutlinedIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <MenuDrawer isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} />
      {/* <!-- Section - Intro --> */}
      <section className={themeStyles['sec-intro']}>
        <div className={themeStyles['fl-grid']}>
          <div className={themeStyles['fl-col']}>
            <div className={themeStyles['fl-col-inner-column']}>
              <div className="fnt-title clr-primary m-b-10">Easy. Fun. Engaging.</div>
              <h2 className="clr-primary m-b-10">Online literacy tools that reach your students.</h2>
              <img src={ImageLogo} alt="" />
            </div>
          </div>
        </div>
      </section>
      {/* <!-- section position statement --> */}
      <section className={`${themeStyles['sec-position']} ${themeStyles['sec-padding-tb']}`}>
        <div className={`${themeStyles['fl-grid']} ${themeStyles['fl-grid-wrap-reverse']}`}>
          <div className={themeStyles['fl-col']}>
            <img src={ImageStudent} className={themeStyles['img-responsive']} alt="" />
          </div>
          <div className={themeStyles['fl-col']}>
            <h2>
              Developed by the University of Florida Lastinger Center, the Flamingo Literacy Teaching Platform allows
              reading instructors to transition Small-Group Reading to a virtual setting.
            </h2>
          </div>
        </div>
      </section>
      {/* <!-- section - USPs --> */}
      <section className={`${themeStyles['sec-usps']} ${themeStyles['sec-padding-tb']}`}>
        <div className={themeStyles['fl-grid']}>
          {/* <!-- USP --> */}
          <div className={themeStyles['usp']}>
            <div className={themeStyles['usp-inner']}>
              {/* <!-- icon --> */}
              <div className={`${themeStyles['usp-icon']} ${themeStyles['usp-icon-books']}`}></div>
              <h2 className="fnt-xbold clr-white">Digital Library</h2>
              <h4 className="fnt-light clr-white">
                Over 900 books in English and Spanish organized by Early Intervention level, Guided Reading level, or
                Lexile level.
              </h4>
            </div>
          </div>
          {/* <!-- USP --> */}
          <div className={themeStyles['usp']}>
            <div className={themeStyles['usp-inner']}>
              {/* <!-- icon --> */}
              <div className={`${themeStyles['usp-icon']} ${themeStyles['usp-icon-tools']}`}></div>
              <h2 className="fnt-xbold clr-white">Lesson Builder</h2>
              <h4 className="fnt-light clr-white">
                Build lessons for virtual small-group instruction quickly and easily.
              </h4>
            </div>
          </div>
          {/* <!-- USP --> */}
          <div className={themeStyles['usp']}>
            <div className={themeStyles['usp-inner']}>
              {/* <!-- icon --> */}
              <div className={`${themeStyles['usp-icon']} ${themeStyles['usp-icon-responsive']}`}></div>
              <h2 className="fnt-xbold clr-white">Accessibility</h2>
              <h4 className="fnt-light clr-white">Accessible on computers and tablets. Touch-screen interface.</h4>
            </div>
          </div>
          {/* <!-- USP --> */}
          <div className={themeStyles['usp']}>
            <div className={themeStyles['usp-inner']}>
              {/* <!-- icon --> */}
              <div className={`${themeStyles['usp-icon']} ${themeStyles['usp-icon-target']}`}></div>
              <h2 className="fnt-xbold clr-white">Evidence-Based</h2>
              <h4 className="fnt-light clr-white">
                Incorporates tools for evidence-based reading instruction such as manipulative letters and Elkonin
                boxes.
              </h4>
            </div>
          </div>
        </div>
      </section>
      {/* <!-- section footer --> */}
      <section className={`${themeStyles['sec-footer']} bg-light-blue clr-dark-blue`}>
        <div className={themeStyles['fl-grid']}>
          <div className={themeStyles['fl-col']}>
            <h6>Copyright 2020 University of Florida Lastinger Center</h6>
          </div>
          <div className={themeStyles['fl-col']}>
            <div className={`${themeStyles['fl-col-inner-row']} ${themeStyles['footer-icons']}`}>
              <a href="https://www.facebook.com/LastingerCenter" target="_blank" rel="noopener noreferrer">
                <div className={`${themeStyles['footer-icon']} ${themeStyles['footer-icon-facebook']}`}></div>
              </a>
              <a href="https://twitter.com/lastingercenter" target="_blank" rel="noopener noreferrer">
                <div className={`${themeStyles['footer-icon']} ${themeStyles['footer-icon-twitter']}`}></div>
              </a>
              <a href="https://www.instagram.com/lastingercenter" target="_blank" rel="noopener noreferrer">
                <div className={`${themeStyles['footer-icon']} ${themeStyles['footer-icon-instagram']}`}></div>
              </a>
              <a href="https://www.linkedin.com/company/lastingercenter" target="_blank" rel="noopener noreferrer">
                <div className={`${themeStyles['footer-icon']} ${themeStyles['footer-icon-linkedin']}`}></div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </Root>
  );
}
